import React from "react";
import PropTypes from "prop-types";
import { Alert, AlertTitle, Box, Button, Stack, Typography } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useNavigate } from "react-router-dom";

/**
 * Shown above any registration form when /accounts-by-email returns at least
 * one match for the email the user typed. Lists the masked usernames found
 * and offers two recovery paths so the user does NOT create a new duplicate.
 */
const DuplicateEmailAlert = ({ accounts, email }) => {
  const navigate = useNavigate();
  if (!accounts || accounts.length === 0) return null;

  const goLogin = () => {
    if (email) {
      try {
        sessionStorage.setItem("prefillEmail", email);
      } catch (err) {
        console.debug("sessionStorage unavailable", err);
      }
    }
    navigate("/");
  };

  const goForgotUsername = () => {
    if (email) {
      try {
        sessionStorage.setItem("prefillEmail", email);
      } catch (err) {
        console.debug("sessionStorage unavailable", err);
      }
    }
    navigate("/?action=forgot-username");
  };

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>An account already exists for this email</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        We found {accounts.length} account{accounts.length === 1 ? "" : "s"} associated with{" "}
        <strong>{email}</strong>. To avoid creating a duplicate, please sign in or recover your
        username instead of registering again.
      </Typography>
      <Box sx={{ mb: 1 }}>
        {accounts.map((acc) => (
          <Typography key={acc.user_id} variant="caption" component="div" sx={{ color: "text.secondary" }}>
            • {acc.username}
            {acc.role ? ` — ${acc.role}` : ""}
            {acc.lawfirm_or_pg_label ? ` @ ${acc.lawfirm_or_pg_label}` : ""}
          </Typography>
        ))}
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button size="small" variant="contained" startIcon={<LoginIcon />} onClick={goLogin}>
          Go to Sign In
        </Button>
        <Button size="small" variant="outlined" startIcon={<HelpOutlineIcon />} onClick={goForgotUsername}>
          Forgot username?
        </Button>
      </Stack>
    </Alert>
  );
};

DuplicateEmailAlert.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      username: PropTypes.string,
      role: PropTypes.string,
      lawfirm_or_pg_label: PropTypes.string,
    })
  ),
  email: PropTypes.string,
};

export default DuplicateEmailAlert;
